<template>
  <n-card title="Importer une facture">
    <n-space vertical>
      <!-- Mode selector -->
      <n-tabs v-model:value="mode" type="line">
        <n-tab name="html">Coller l'email (HTML)</n-tab>
        <n-tab name="pdf">Uploader un PDF</n-tab>
      </n-tabs>

      <!-- HTML mode -->
      <div v-if="mode === 'html'">
        <n-input
          v-model:value="htmlContent"
          type="textarea"
          placeholder="Collez ici le contenu HTML de votre email de confirmation Leclerc Drive..."
          :rows="8"
        />
      </div>

      <!-- PDF mode -->
      <div v-if="mode === 'pdf'">
        <n-upload
          accept=".pdf"
          :max="1"
          @change="onFileChange"
        >
          <n-button>Choisir un fichier PDF</n-button>
        </n-upload>
        <n-text v-if="pdfFile" depth="3" style="margin-top:8px">
          {{ pdfFile.name }}
        </n-text>
      </div>

      <n-space>
        <n-button
          type="primary"
          :loading="parsing"
          :disabled="!canParse"
          @click="parse"
        >
          Analyser
        </n-button>
        <n-button v-if="preview" @click="reset">Réinitialiser</n-button>
      </n-space>

      <!-- Error -->
      <n-alert v-if="parseError" type="error" :title="parseError" />

      <!-- Preview -->
      <template v-if="preview">
        <n-divider>Aperçu — vérifiez avant de sauvegarder</n-divider>

        <n-descriptions bordered :column="2" label-placement="left">
          <n-descriptions-item label="Date">{{ preview.date }}</n-descriptions-item>
          <n-descriptions-item label="Commerçant">{{ preview.merchant }}</n-descriptions-item>
          <n-descriptions-item label="Catégorie">{{ preview.category ?? '—' }}</n-descriptions-item>
          <n-descriptions-item label="Total">{{ formatCurrency(preview.total_amount) }}</n-descriptions-item>
        </n-descriptions>

        <n-table v-if="preview.line_items.length" :bordered="false" size="small" style="margin-top:12px">
          <thead>
            <tr>
              <th>Article</th>
              <th>Qté</th>
              <th>Prix unitaire</th>
              <th>Sous-total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in preview.line_items" :key="i">
              <td>{{ item.description }}</td>
              <td>{{ item.quantity }}</td>
              <td>{{ formatCurrency(item.unit_price) }}</td>
              <td>{{ formatCurrency(item.unit_price * item.quantity) }}</td>
            </tr>
          </tbody>
        </n-table>
        <n-text v-else depth="3">Aucune ligne d'article détectée.</n-text>

        <div style="margin-top:16px; text-align:right">
          <n-button type="primary" :loading="saving" @click="save">
            Sauvegarder la transaction
          </n-button>
        </div>
      </template>
    </n-space>
  </n-card>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useTransactionStore } from '../stores/transactionStore';

const emit = defineEmits(['saved']);
const store = useTransactionStore();

const mode = ref('html');
const htmlContent = ref('');
const pdfFile = ref(null);
const preview = ref(null);
const parsing = ref(false);
const saving = ref(false);
const parseError = ref(null);

const canParse = computed(() =>
  mode.value === 'html' ? htmlContent.value.trim().length > 0 : pdfFile.value !== null
);

const onFileChange = ({ fileList }) => {
  pdfFile.value = fileList[0]?.file ?? null;
};

const reset = () => {
  preview.value = null;
  parseError.value = null;
};

const parse = async () => {
  parseError.value = null;
  parsing.value = true;
  try {
    const fd = new FormData();
    if (mode.value === 'html') {
      fd.append('html_content', htmlContent.value);
    } else {
      fd.append('file', pdfFile.value);
    }
    preview.value = await store.previewInvoice(fd);
  } catch (e) {
    parseError.value = e.message;
  } finally {
    parsing.value = false;
  }
};

const save = async () => {
  if (!preview.value) return;
  saving.value = true;
  try {
    await store.addTransaction({
      date: new Date(preview.value.date).getTime(),
      merchant: preview.value.merchant,
      category: preview.value.category,
      account_type_target: preview.value.account_type_target,
      total_amount: preview.value.total_amount,
      notes: preview.value.notes ?? null,
      line_items: preview.value.line_items.map(i => ({
        description: i.description,
        quantity: i.quantity,
        unit_price: i.unit_price,
      })),
    });
    reset();
    htmlContent.value = '';
    pdfFile.value = null;
    emit('saved');
  } catch (e) {
    parseError.value = e.message;
  } finally {
    saving.value = false;
  }
};

const formatCurrency = (v) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v);
</script>
